import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { buildPath, SITE_URL } from "@/lib/routes";
import { usePage } from "@/context/PageContext";

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href, hreflang) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function Seo({
  routeKey = "home",
  params = {},
  lang = "da",
  title,
  description,
  image,
  type = "website",
  jsonLd = [],
}) {
  const { pathname } = useLocation();
  const { setPage } = usePage();

  useEffect(() => {
    setPage({ routeKey, params });
  }, [routeKey, JSON.stringify(params)]); // eslint-disable-line

  useEffect(() => {
    if (title) document.title = title;
    document.documentElement.setAttribute("lang", lang === "da" ? "da" : "en");

    upsertMeta("name", "description", description);
    const canonical = `${SITE_URL}${pathname}`;
    upsertLink("canonical", canonical);

    // hreflang alternates
    const daPath = buildPath(routeKey, "da", params);
    const enPath = buildPath(routeKey, "en", params);
    upsertLink("alternate", `${SITE_URL}${daPath}`, "da");
    upsertLink("alternate", `${SITE_URL}${enPath}`, "en");
    upsertLink("alternate", `${SITE_URL}${daPath}`, "x-default");

    // Open Graph
    const ogImage = image
      ? (image.startsWith("http") ? image : `${SITE_URL}${image}`)
      : `${SITE_URL}/og-default.png`;
    const ogAlt = title || "Magnus Løv Schmidt — SEO Specialist";
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:site_name", "Magnus Løv Schmidt");
    upsertMeta("property", "og:locale", lang === "da" ? "da_DK" : "en_US");
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("property", "og:image:width", "1200");
    upsertMeta("property", "og:image:height", "630");
    upsertMeta("property", "og:image:type", "image/png");
    upsertMeta("property", "og:image:alt", ogAlt);

    // Twitter
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", ogImage);
    upsertMeta("name", "twitter:image:alt", ogAlt);
    upsertMeta("name", "twitter:site", "@MagnusLSchmidt");
    upsertMeta("name", "twitter:creator", "@MagnusLSchmidt");

    // JSON-LD
    document.head
      .querySelectorAll('script[data-seo-jsonld="true"]')
      .forEach((n) => n.remove());
    jsonLd.filter(Boolean).forEach((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-jsonld", "true");
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
    });
  }, [pathname, title, description, image, type, JSON.stringify(jsonLd)]); // eslint-disable-line

  return null;
}
